// src/padplanner/planRefusals.js
//
// The words for each refusal a PadPlanner route can return, by its code.
// Moved out of PlanPage.jsx so that file exports only its component (the
// react-refresh rule), and so New plan's one-action create can say exactly
// which step was refused in the same words.

/** What to say for each refusal the plan routes can return. */
export const REFUSAL_COPY = Object.freeze({
  perimeter_too_large: "That perimeter is larger than 1 square mile, the limit for a plan.",
  perimeter_too_many_vertices: "That perimeter has more than 2,000 corners. Simplify it and try again.",
  perimeter_invalid: "That perimeter crosses itself or has a broken ring.",
  perimeter_not_polygon: "A perimeter must be an area (a polygon).",
  perimeter_unparseable: "That perimeter couldn't be read.",
  perimeter_feature_count: "A perimeter file must contain exactly one feature, and it must be a polygon.",
  perimeter_required: "Set the perimeter before pinning imagery.",
  imagery_not_available: "That imagery doesn't cover this plan's whole perimeter.",
  imagery_straddles_footprints: "No single imagery capture covers this whole perimeter; it crosses the edge between captures. Redraw it inside one capture to pin imagery.",
  imagery_incomplete: "That imagery has no capture date or detail level recorded, so it can't be pinned.",
  pad_not_found: "That pad isn't available to your organization.",
  well_not_in_registry: "That API number isn't in the RRC registry. Add it as a planned (manual) well if it isn't permitted yet.",
  api_number_not_texas: "Only Texas wells (API state code 42) can be added.",
  api_number_invalid: "An API number has 8, 10 or 14 digits.",
  api_lookup_too_short: "Type at least 5 digits of the API number.",
  too_many_wells: "A plan lists at most 100 wells.",
  plan_archived: "This plan is archived and can't be changed.",
  project_invalid: "That project isn't an active project of your organization.",
  revision_frozen: "This revision has been submitted and can't be changed.",
  export_not_audited: "The download couldn't be recorded, so it wasn't sent. Try again.",
  report_not_audited: "The report view couldn't be recorded, so it wasn't sent. Try again.",
  unsupported_format: "Use GeoJSON, KML, KMZ or a zipped shapefile.",
  no_extension: "Use GeoJSON, KML, KMZ or a zipped shapefile.",
  file_too_large: "That file is larger than 50 MB.",
  empty_file: "That file is empty.",
  crs_undeclared: "That shapefile has no .prj, so its coordinates can't be placed. Include the .prj in the zip.",
  no_layers: "No map layers were found in that file.",
  unreadable_source: "That file couldn't be read as map data.",
  template_set_changed: "The template library changed while the plan was being created. Try again.",
  // The template library (S3).
  library_admin_required: "Only an organization admin can change the template library.",
  support_session_read_only: "A support session can view the template library but can't change it.",
  set_standard_readonly: "The AI PRIME standard set is read-only. Start your own set to customize templates.",
  set_not_draft: "This set is published or retired, so its contents can't change. Start a new version instead.",
  set_not_published: "Only a published set can be retired. A draft is discarded instead.",
  set_empty: "Add at least one template before publishing.",
  draft_exists: "This set already has a draft version. Edit or discard it first.",
  set_name_invalid: "A set name is 1 to 80 characters.",
  lineage_invalid: "The default must be one of your organization's sets with a published version.",
  template_key_taken: "Another template in this set already has that key.",
  template_key_invalid: "A key is 1 to 48 lowercase letters, digits or underscores, starting with a letter or digit.",
  template_name_invalid: "A template name is 1 to 80 characters.",
  template_category_invalid: "Choose a category.",
  template_kind_invalid: "A template is a footprint (length and width) or a zone (radius).",
  template_dims_invalid: "Length and width are each 0.5 to 1,000 feet.",
  template_radius_invalid: "A zone's radius is 1 to 2,640 feet.",
  template_dims_source_invalid: "A source is 1 to 500 characters.",
  layer_invalid: "A layer is 1 to 40 lowercase letters, digits or underscores.",
  too_many_templates: "A set holds at most 500 templates.",
  // Submit to landowner (S6b): the POST's codes, in landowner.js's words.
  not_submittable: "Only an approved or issued version can be submitted to a landowner.",
  no_landowner_relationship: "No landowner relationship — contact the landowner or DreamHammer.",
  grant_revoked: "The landowner no longer accepts submissions from your organization — contact the landowner or DreamHammer.",
  landowner_submit_not_permitted: "Your seat can't submit plans to a landowner.",
  already_submitted: "This version has already been submitted to that landowner.",
  landowner_review_open: "That landowner is still reviewing the last submission. Wait for their answer, then submit again.",
  owner_is_this_org: "A plan is submitted to another organization, not to your own.",
  owner_org_id_invalid: "Choose a landowner.",
  // The intended landowner (158): create, Edit details and the list's filter.
  landowner_already_submitted: "This plan has been submitted to a landowner, so its intended landowner no longer changes.",
  intended_landowner_invalid: "Choose a landowner from the list.",
  landowner_invalid: "That landowner filter isn't one the list knows.",
});
export function refusalText(err) {
  const text = (err?.code && REFUSAL_COPY[err.code]) || err?.message || "Something went wrong.";
  // The server names where the ring fails (ST_IsValidDetail), as [lon, lat].
  const at = err?.code === "perimeter_invalid" ? err.detail?.location : null;
  if (Array.isArray(at) && at.length === 2 && at.every(Number.isFinite)) {
    return `${text.replace(/\.$/, "")} near ${at[1].toFixed(5)}, ${at[0].toFixed(5)}.`;
  }
  return text;
}
